import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { UserCheck, Clock, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

const hhmm = (iso) => new Date(iso).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" });

/* Compact "Nu aanwezig" strip for the Werkbord — fed by the kiosk time clock. */
export function PresencePanel({ canManage }) {
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["presence"], queryFn: () => api.get("/timeclock/presence").then((r) => r.data), refetchInterval: 60000, retry: false });
  const emps = data?.employees || [];
  if (!emps.length) return null;
  const present = emps.filter((e) => e.status === "in");
  const gone = emps.filter((e) => e.status === "out");
  const absent = emps.filter((e) => e.status === "absent");
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="presence-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground"><UserCheck className="h-4 w-4 text-emerald-500" />{t("presentNow")} <span className="text-foreground font-bold" data-testid="presence-count">{present.length}</span>/{emps.length}</div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {gone.length > 0 && <span data-testid="presence-out">{t("clockedOutToday")}: {gone.map((e) => e.name.split(" ")[0]).join(", ")}</span>}
          {absent.length > 0 && <span data-testid="presence-absent">{t("notClockedIn")}: {absent.map((e) => e.name.split(" ")[0]).join(", ")}</span>}
          {canManage && <Link to="/hours" className="underline hover:text-foreground" data-testid="presence-hours-link">{t("hoursTitle")}</Link>}
        </div>
      </div>
      {present.length === 0 ? <div className="text-sm text-muted-foreground" data-testid="presence-empty">{t("nobodyClockedIn")}</div> : (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {present.map((e) => (
            <div key={e.id} className="shrink-0 min-w-[180px] rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2" data-testid={`presence-${e.id}`}>
              <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /><span className="font-semibold truncate">{e.name}</span></div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{e.department}</div>
              <div className="flex items-center justify-between mt-1.5 text-[11px]">
                <span className="flex items-center gap-1 font-mono text-emerald-700 dark:text-emerald-400"><Clock className="h-3 w-3" />{t("since")} {hhmm(e.since)}</span>
                <span className={`flex items-center gap-1 font-mono ${e.open_jobs === 0 ? "text-emerald-600 font-bold" : e.busy_jobs > 0 ? "text-amber-600" : "text-muted-foreground"}`} title={t("openJobs")}><Wrench className="h-3 w-3" />{e.open_jobs === 0 ? t("free") : `${e.open_jobs} ${t("jobsShort")}`}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
